// web/src/pages/Dmca.tsx
import type { JSX, ReactNode } from "react";
import { Link } from "react-router-dom";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";

const EFFECTIVE_DATE_TEXT = "August 21, 2025";
const LAST_UPDATED_TEXT = "August 21, 2025";

// Contact info
const SUPPORT_EMAIL = "support@spellgather.com";
const COPYRIGHT_AGENT_EMAIL = "support@spellgather.com"; // routed internally to the designated agent

function Section({
  id,
  title,
  children,
}: {
  id: string;
  title: string;
  children: ReactNode;
}): JSX.Element {
  return (
    <section id={id} className="scroll-mt-24">
      <h2 className="text-xl sm:text-2xl font-semibold text-white mt-8 mb-3">
        {title}
      </h2>
      <div className="prose prose-invert max-w-none">{children}</div>
    </section>
  );
}

export default function Dmca(): JSX.Element {
  return (
    <main className="mx-auto max-w-4xl p-6">
      <Card className="bg-gray-900 border-gray-700 shadow-xl">
        <CardHeader>
          <CardTitle className="text-white">DMCA Policy</CardTitle>
          <CardDescription className="text-white/90">
            Effective: {EFFECTIVE_DATE_TEXT} · Last Updated: {LAST_UPDATED_TEXT}
          </CardDescription>
        </CardHeader>

        <CardContent className="text-white">
          {/* TOC */}
          <nav aria-label="Table of contents" className="mb-6">
            <ul className="text-sm grid sm:grid-cols-2 gap-2">
              <li>
                <a className="underline" href="#overview">
                  1. Overview
                </a>
              </li>
              <li>
                <a className="underline" href="#designated-agent">
                  2. Designated Agent
                </a>
              </li>
              <li>
                <a className="underline" href="#submit-notice">
                  3. How to Submit a DMCA Notice
                </a>
              </li>
              <li>
                <a className="underline" href="#template">
                  4. DMCA Notice Template
                </a>
              </li>
              <li>
                <a className="underline" href="#counter-notice">
                  5. Counter-Notice (Restoration)
                </a>
              </li>
              <li>
                <a className="underline" href="#counter-template">
                  6. Counter-Notice Template
                </a>
              </li>
              <li>
                <a className="underline" href="#repeat">
                  7. Repeat Infringer Policy
                </a>
              </li>
              <li>
                <a className="underline" href="#subpoena">
                  8. 17 U.S.C. §512(h) Subpoenas
                </a>
              </li>
              <li>
                <a className="underline" href="#bad-faith">
                  9. Bad-Faith Notices (17 U.S.C. §512(f))
                </a>
              </li>
              <li>
                <a className="underline" href="#faq">
                  10. Quick FAQ
                </a>
              </li>
              <li>
                <a className="underline" href="#changes">
                  11. Changes to this Policy
                </a>
              </li>
              <li>
                <a className="underline" href="#contact">
                  Contact
                </a>
              </li>
            </ul>
          </nav>

          <Section id="overview" title="1) Overview">
            <p>
              Spellgather respects the intellectual property rights of others
              and follows the notice-and-takedown procedures of the Digital
              Millennium Copyright Act (DMCA), 17 U.S.C. §512. This page
              explains how rightsholders can report alleged infringements and
              how users can respond if their content is removed by mistake or
              misidentification.
            </p>
            <p>
              This policy works together with our{" "}
              <Link className="underline" to="/copyright">
                Copyright &amp; Fan Content Notice
              </Link>
              ,{" "}
              <Link className="underline" to="/terms">
                Terms of Service
              </Link>
              ,{" "}
              <Link className="underline" to="/community">
                Community Guidelines
              </Link>
              , and{" "}
              <Link className="underline" to="/privacy">
                Privacy Policy
              </Link>
              .
            </p>
          </Section>

          <Section id="designated-agent" title="2) Designated Agent">
            <p>
              DMCA notices and counter-notices should be sent to our designated
              agent:
            </p>
            <ul>
              <li>
                <strong>Email:</strong>{" "}
                <a
                  className="underline"
                  href={`mailto:${COPYRIGHT_AGENT_EMAIL}?subject=DMCA%20Notice`}
                >
                  {COPYRIGHT_AGENT_EMAIL}
                </a>
              </li>
            </ul>
            <p>
              We accept notices by email to expedite processing. We may share
              the complete notice with the user who posted the content and, if
              necessary, publish an anonymized version for transparency.
            </p>
          </Section>

          <Section id="submit-notice" title="3) How to Submit a DMCA Notice">
            <p className="mb-2">
              If you are a copyright owner or authorized agent, your notice must
              include <em>all</em> elements required by 17 U.S.C. §512(c)(3):
            </p>
            <ol>
              <li>
                A physical or electronic signature of the copyright owner or
                authorized agent.
              </li>
              <li>
                Identification of the copyrighted work claimed to have been
                infringed, or a representative list.
              </li>
              <li>
                Identification of the material that is claimed to be infringing,
                with information reasonably sufficient to permit us to locate
                the material (e.g., profile URL, message ID, item URL).
              </li>
              <li>
                Your contact information: full name, mailing address, telephone
                number, and email address.
              </li>
              <li>
                A statement that you have a good-faith belief that the use of
                the material in the manner complained of is not authorized by
                the copyright owner, its agent, or the law.
              </li>
              <li>
                A statement that the information in the notification is
                accurate, and under penalty of perjury, that you are authorized
                to act on behalf of the owner.
              </li>
            </ol>
            <p className="mt-2">
              Incomplete notices may be rejected. Upon receiving a compliant
              notice, we generally remove or disable access to the material and
              notify the user with information about how to submit a
              counter-notice.
            </p>
            <div className="mt-4 flex gap-2">
              <a href={`mailto:${COPYRIGHT_AGENT_EMAIL}?subject=DMCA%20Notice`}>
                <Button className="bg-purple-600 hover:bg-purple-700">
                  Email a DMCA Notice
                </Button>
              </a>
              <Link to="/copyright">
                <Button
                  variant="outline"
                  className="border-slate-600 text-slate-200 hover:bg-slate-800"
                >
                  Read Copyright &amp; Fan Content
                </Button>
              </Link>
            </div>
          </Section>

          <Section id="template" title="4) DMCA Notice Template">
            <p>
              Below is a sample you can adapt. Please replace bracketed text
              with your details.
            </p>
            <pre className="whitespace-pre-wrap bg-black/40 p-4 rounded border border-gray-800 text-sm">
              {`Subject: DMCA Notice

To: Spellgather Copyright Agent <${COPYRIGHT_AGENT_EMAIL}>

I, [full name], am the copyright owner or authorized agent of the owner of certain intellectual property rights.

1. Work(s) claimed infringed:
[Describe the copyrighted work(s) or provide a representative list]

2. Location of the infringing material:
[Provide specific URLs, profile links, message IDs, listing URLs, or other precise location info]

3. Contact information:
Name: [full legal name]
Company (if applicable): [company]
Address: [street, city, state/province, postal code, country]
Phone: [phone number]
Email: [email address]

4. Good-faith statement:
I have a good-faith belief that the use of the material in the manner complained of is not authorized by
the copyright owner, its agent, or the law.

5. Accuracy and authority:
The information in this notice is accurate, and under penalty of perjury, I am the copyright owner or
am authorized to act on behalf of the owner of an exclusive right that is allegedly infringed.

Signature:
[Typed full name or electronic signature]
Date: [YYYY-MM-DD]`}
            </pre>
          </Section>

          <Section id="counter-notice" title="5) Counter-Notice (Restoration)">
            <p className="mb-2">
              If your content was removed under a DMCA notice and you believe
              the removal was erroneous or the material was misidentified, you
              may submit a counter-notice under 17 U.S.C. §512(g). Your
              counter-notice must include:
            </p>
            <ol>
              <li>Your physical or electronic signature.</li>
              <li>
                Identification of the material removed and the location where it
                appeared before removal.
              </li>
              <li>
                A statement, under penalty of perjury, that you have a
                good-faith belief the material was removed or disabled as a
                result of mistake or misidentification.
              </li>
              <li>
                Your name, address, and phone number, and a statement that you
                consent to the jurisdiction of the Federal District Court for
                your address, or if outside the U.S., any judicial district in
                which we may be found, and that you will accept service of
                process from the original complainant or their agent.
              </li>
            </ol>
            <p className="mt-2">
              After we receive a valid counter-notice, we generally restore the
              material in 10–14 business days unless the complainant informs us
              that a court action has been filed seeking to restrain the
              allegedly infringing activity.
            </p>
            <div className="mt-4">
              <a
                href={`mailto:${COPYRIGHT_AGENT_EMAIL}?subject=DMCA%20Counter-Notice`}
              >
                <Button
                  variant="outline"
                  className="border-slate-600 text-slate-200 hover:bg-slate-800"
                >
                  Email a Counter-Notice
                </Button>
              </a>
            </div>
          </Section>

          <Section id="counter-template" title="6) Counter-Notice Template">
            <p>You can use the following template for your counter-notice:</p>
            <pre className="whitespace-pre-wrap bg-black/40 p-4 rounded border border-gray-800 text-sm">
              {`Subject: DMCA Counter-Notice

To: Spellgather Copyright Agent <${COPYRIGHT_AGENT_EMAIL}>

I, [full name], submit this counter-notification in response to the removal of material.

1. Material removed:
[Describe the material and where it appeared on Spellgather before removal (URL, profile link, listing ID, etc.)]

2. Good-faith statement:
Under penalty of perjury, I have a good-faith belief that the material was removed or disabled as a result of
mistake or misidentification.

3. Contact information and consent to jurisdiction:
Name: [full legal name]
Address: [street, city, state/province, postal code, country]
Phone: [phone number]
Email: [email address]
I consent to the jurisdiction of the Federal District Court for my address (or, if outside the U.S.,
any judicial district in which Spellgather may be found), and I will accept service of process from the person
who provided the original DMCA notice or their agent.

Signature:
[Typed full name or electronic signature]
Date: [YYYY-MM-DD]`}
            </pre>
          </Section>

          <Section id="repeat" title="7) Repeat Infringer Policy">
            <p>
              In appropriate circumstances and at our discretion, Spellgather
              may terminate or restrict accounts of users determined to be
              repeat infringers. Measures may also include content removal,
              feature limits, or other actions consistent with our{" "}
              <Link className="underline" to="/terms">
                Terms of Service
              </Link>{" "}
              and{" "}
              <Link className="underline" to="/community">
                Community Guidelines
              </Link>
              .
            </p>
          </Section>

          <Section id="subpoena" title="8) 17 U.S.C. §512(h) Subpoenas">
            <p>
              Rightsholders seeking identifying information about a user in
              connection with an alleged infringement may pursue a subpoena
              under 17 U.S.C. §512(h). We will evaluate and respond to properly
              served legal process consistent with applicable law and our{" "}
              <Link className="underline" to="/privacy">
                Privacy Policy
              </Link>
              .
            </p>
          </Section>

          <Section
            id="bad-faith"
            title="9) Bad-Faith Notices (17 U.S.C. §512(f))"
          >
            <p>
              Submitting a knowingly false or materially misleading DMCA notice
              or counter-notice may expose the sender to liability under 17
              U.S.C. §512(f) and other laws. If you are unsure whether a use is
              authorized, consider consulting an attorney before filing.
            </p>
          </Section>

          <Section id="faq" title="10) Quick FAQ">
            <h3 className="text-lg font-semibold">
              Will you forward my notice to the uploader?
            </h3>
            <p>
              Yes. We typically share the notice (with contact details) so the
              uploader can evaluate and respond.
            </p>

            <h3 className="text-lg font-semibold mt-4">
              How fast do you act on notices?
            </h3>
            <p>
              We aim to review complete notices promptly. Timing varies based on
              volume and complexity.
            </p>

            <h3 className="text-lg font-semibold mt-4">
              Can I retract a notice?
            </h3>
            <p>
              Yes—email us from the same address with the original notice
              details. We will consider restoration steps.
            </p>

            <h3 className="text-lg font-semibold mt-4">
              What if the content involves trademarks or fan content?
            </h3>
            <p>
              See our{" "}
              <Link className="underline" to="/copyright">
                Copyright &amp; Fan Content Notice
              </Link>{" "}
              for guidance on non-copyright IP issues and fan-content practices.
            </p>
          </Section>

          <Section id="changes" title="11) Changes to this Policy">
            <p>
              We may update this page to reflect law or platform changes.
              Material updates may be announced in-app and/or by email where
              appropriate. Continued use after the effective date indicates
              acceptance.
            </p>
          </Section>

          <Section id="contact" title="Contact">
            <p>
              Copyright Agent:{" "}
              <a className="underline" href={`mailto:${COPYRIGHT_AGENT_EMAIL}`}>
                {COPYRIGHT_AGENT_EMAIL}
              </a>
            </p>
            <p className="mt-2">
              General support:{" "}
              <a className="underline" href={`mailto:${SUPPORT_EMAIL}`}>
                {SUPPORT_EMAIL}
              </a>
            </p>
            <div className="mt-4 flex flex-wrap gap-2">
              <a href={`mailto:${COPYRIGHT_AGENT_EMAIL}?subject=DMCA%20Notice`}>
                <Button className="bg-purple-600 hover:bg-purple-700">
                  Send DMCA Notice
                </Button>
              </a>
              <a
                href={`mailto:${COPYRIGHT_AGENT_EMAIL}?subject=DMCA%20Counter-Notice`}
              >
                <Button
                  variant="outline"
                  className="border-slate-600 text-slate-200 hover:bg-slate-800"
                >
                  Send Counter-Notice
                </Button>
              </a>
              <Link to="/copyright">
                <Button
                  variant="outline"
                  className="border-slate-600 text-slate-200 hover:bg-slate-800"
                >
                  Read Copyright &amp; Fan Content
                </Button>
              </Link>
            </div>
          </Section>
        </CardContent>
      </Card>
    </main>
  );
}

